import React, { useState } from 'react';
import { Slider } from './ui/slider';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { 
  Download, 
  Save, 
  RotateCcw, 
  Eye, 
  Compass, 
  Grid3X3,
  ChevronRight,
  ChevronLeft,
  Maximize2,
  Lock,
  Unlock,
  Star,
  Flashlight,
  DoorOpen,
  ZoomIn,
  ZoomOut,
  Image,
  Crosshair,
  X,
  User,
  Sparkles,
  Info
} from 'lucide-react';
import { getKuaGroup, getChineseYear, AUSPICIOUS_TYPES, INAUSPICIOUS_TYPES } from '../utils/kuaCalculator';

// Lucky Direction explanations by rank
const LUCKY_EXPLANATIONS = {
  1: {
    title: "SUCCESS",
    text: "This is your most auspicious direction for attracting wealth, success, and overall good fortune. Try to sleep with the crown of your head pointing toward this direction, and sit facing this direction whenever possible. You can also align yourself with this direction during important activities such as meetings, interviews, working at your desk, dining, or even relaxing. Aligning with this direction helps enhance opportunities for wealth, recognition, honor, and positive outcomes."
  },
  2: {
    title: "HEALTH",
    text: "This is your second most favorable direction and is associated with well-being and vitality. Facing or sleeping toward this direction can support better health and recovery. It is especially beneficial if you are experiencing health challenges. Elderly individuals may also benefit from sleeping with their head oriented toward this direction to promote balance and wellness."
  },
  3: {
    title: "HARMONY",
    text: "This is your third favorable direction and is connected to relationships, harmony, and emotional well-being. It is particularly supportive for marriage, romance, and strengthening personal relationships. Sitting or sleeping facing this direction may help cultivate smoother communication, mutual understanding, and emotional balance."
  },
  4: {
    title: "DEFENCE",
    text: "This is your fourth favorable direction and is associated with stability, personal development, and inner strength. It is a good direction for studying, self-improvement, meditation, and education. If your first three favorable directions are not practical to use, this direction can still offer supportive energy for growth and protection."
  }
};

// Unlucky Direction explanations by rank
const UNLUCKY_EXPLANATIONS = {
  1: {
    title: "CONFLICT",
    text: "This is considered your most unfavorable direction and may bring significant challenges, conflicts, or instability if strongly activated. It is generally best to avoid aligning important areas such as your main entrance, workspace, or sleeping direction toward this sector. If this direction cannot be avoided, adjustments and Feng Shui remedies may be considered to help reduce its negative impact."
  },
  2: {
    title: "LOSSES",
    text: "This is your third most unfavorable direction and is associated with potential losses or setbacks. These may appear in areas such as finances, personal relationships, or well-being. Situations like misunderstandings, minor illnesses, or financial leakage may arise. The influence may become stronger if your main door or frequently used spaces face this direction."
  },
  3: {
    title: "PROBLEMS",
    text: "This is your second most unfavorable direction and may lead to challenges such as disputes, stress, or unexpected difficulties. Prolonged exposure to this direction may also contribute to health concerns or complications in important matters. It is advisable to avoid facing or aligning with this direction whenever possible."
  },
  4: {
    title: "FAILURE",
    text: "This direction corresponds to Huo Hai, the least harmful among your four unfavorable directions. While it may bring minor obstacles, misunderstandings, or small inconveniences, the effects are generally manageable. If your favorable directions are not available, this direction may still be used as a last alternative."
  }
};

export const ControlPanel = ({
  rotation,
  onRotationChange,
  opacity,
  onOpacityChange,
  showCenterLines,
  onShowCenterLinesChange,
  centerBox,
  confirmedBox,
  isDrawingCenterBox,
  onStartDrawCenterBox,
  onClearCenterBox,
  onConfirmCenterBox,
  onClearConfirmedBox,
  showCompass,
  onShowCompassChange,
  onExport,
  onSave,
  onReset,
  isCollapsed,
  onToggleCollapse,
  compassSize,
  onCompassSizeChange,
  isLocked = false,
  onToggleLock,
  showFlyingStar = false,
  onToggleFlyingStar,
  flyingStarScale = 1,
  onFlyingStarScaleChange,
  highlightedDirections = [],
  onToggleHighlight,
  floorPlanRotation = 0,
  onFloorPlanRotationChange,
  mainDoor = null,
  isDoorSelected = false,
  onDoorRotationChange,
  onDoorOpacityChange,
  onDoorSizeChange,
  showCompassVisual = true,
  onToggleCompassVisual,
  floorPlanZoom = 100,
  onFloorPlanZoomIn,
  onFloorPlanZoomOut,
  isFloorPlanLocked = false,
  isPro = false,
  // Kua Analysis props
  kuaAnalysisEnabled = false,
  onKuaAnalysisToggle,
  birthDate = '1990-01-15',
  onBirthDateChange,
  gender = 'male',
  onGenderChange,
  kuaNumber = null,
  kuaDirections = null,
  className = '',
}) => {
  // 24 Mountains data for the highlight toggles
  // Based on Joey Yap's degree reference
  const MOUNTAINS_24 = [
    { index: 0, name: '壬', pinyin: 'Ren', direction: 'N1', degrees: '337.6-352.5' },
    { index: 1, name: '子', pinyin: 'Zi', direction: 'N2', degrees: '352.6-7.5' },
    { index: 2, name: '癸', pinyin: 'Gui', direction: 'N3', degrees: '7.6-22.5' },
    { index: 3, name: '丑', pinyin: 'Chou', direction: 'NE1', degrees: '22.6-37.5' },
    { index: 4, name: '艮', pinyin: 'Gen', direction: 'NE2', degrees: '37.6-52.5' },
    { index: 5, name: '寅', pinyin: 'Yin', direction: 'NE3', degrees: '52.6-67.5' },
    { index: 6, name: '甲', pinyin: 'Jia', direction: 'E1', degrees: '67.6-82.5' },
    { index: 7, name: '卯', pinyin: 'Mao', direction: 'E2', degrees: '82.6-97.5' },
    { index: 8, name: '乙', pinyin: 'Yi', direction: 'E3', degrees: '97.6-112.5' },
    { index: 9, name: '辰', pinyin: 'Chen', direction: 'SE1', degrees: '112.6-127.5' },
    { index: 10, name: '巽', pinyin: 'Xun', direction: 'SE2', degrees: '127.6-142.5' },
    { index: 11, name: '巳', pinyin: 'Si', direction: 'SE3', degrees: '142.6-157.5' },
    { index: 12, name: '丙', pinyin: 'Bing', direction: 'S1', degrees: '157.6-172.5' },
    { index: 13, name: '午', pinyin: 'Wu', direction: 'S2', degrees: '172.6-187.5' },
    { index: 14, name: '丁', pinyin: 'Ding', direction: 'S3', degrees: '187.6-202.5' },
    { index: 15, name: '未', pinyin: 'Wei', direction: 'SW1', degrees: '202.6-217.5' },
    { index: 16, name: '坤', pinyin: 'Kun', direction: 'SW2', degrees: '217.6-232.5' },
    { index: 17, name: '申', pinyin: 'Shen', direction: 'SW3', degrees: '232.6-247.5' },
    { index: 18, name: '庚', pinyin: 'Geng', direction: 'W1', degrees: '247.6-262.5' },
    { index: 19, name: '酉', pinyin: 'You', direction: 'W2', degrees: '262.6-277.5' },
    { index: 20, name: '辛', pinyin: 'Xin', direction: 'W3', degrees: '277.6-292.5' },
    { index: 21, name: '戌', pinyin: 'Xu', direction: 'NW1', degrees: '292.6-307.5' },
    { index: 22, name: '乾', pinyin: 'Qian', direction: 'NW2', degrees: '307.6-322.5' },
    { index: 23, name: '亥', pinyin: 'Hai', direction: 'NW3', degrees: '322.6-337.5' },
  ];

  const handleRotationInput = (e) => {
    let value = parseFloat(e.target.value) || 0;
    value = Math.max(0, Math.min(360, value));
    onRotationChange(value);
  };

  const handleFloorPlanRotationInput = (e) => {
    let value = parseFloat(e.target.value) || 0;
    value = Math.max(-180, Math.min(180, value));
    onFloorPlanRotationChange?.(value);
  };

  const handleDoorRotationInput = (e) => {
    let value = parseFloat(e.target.value) || 0;
    value = Math.max(0, Math.min(360, value));
    onDoorRotationChange?.(value);
  };

  return (
    <div 
      className={`control-panel-wrapper ${className}`}
      style={{
        position: 'fixed',
        right: 0,
        top: 0,
        height: '100%',
        zIndex: 20,
        display: 'flex',
      }}
      data-testid="control-panel"
    >
      {/* Toggle Button */}
      <button
        onClick={onToggleCollapse}
        style={{
          alignSelf: 'center',
          marginLeft: '-32px',
          width: '32px',
          height: '48px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#ffffff',
          border: '1px solid #e5e7eb',
          borderRight: 'none',
          borderRadius: '8px 0 0 8px',
          cursor: 'pointer',
          boxShadow: '-2px 0 8px rgba(0,0,0,0.05)',
        }}
        data-testid="toggle-panel-btn"
      >
        {isCollapsed ? (
          <ChevronLeft size={18} style={{ color: '#2563eb' }} />
        ) : (
          <ChevronRight size={18} style={{ color: '#2563eb' }} />
        )}
      </button>

      {/* Panel Content */}
      <div 
        style={{
          height: '100%',
          overflowY: 'auto',
          transition: 'all 0.3s ease',
          width: isCollapsed ? 0 : '320px',
          opacity: isCollapsed ? 0 : 1,
          backgroundColor: '#ffffff',
          borderLeft: '1px solid #e5e7eb',
          boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.05)',
        }}
      >
        <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '28px' }}>
          {/* Header */}
          <div style={{ 
            textAlign: 'center', 
            paddingBottom: '20px', 
            borderBottom: '1px solid #e5e7eb' 
          }}>
            <h2 style={{ 
              fontSize: '18px', 
              fontWeight: '600', 
              color: '#1f2937',
              marginBottom: '4px',
            }}>
              Compass Controls
            </h2>
            <p style={{ fontSize: '13px', color: '#9ca3af' }}>
              Adjust the Luo Pan overlay
            </p>
          </div>

          {/* Lock/Unlock Compass */}
          {onToggleLock && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Button
                onClick={onToggleLock}
                variant={isLocked ? "default" : "outline"}
                style={{ 
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  backgroundColor: isLocked ? '#dc2626' : 'transparent',
                  borderColor: isLocked ? '#dc2626' : '#e5e7eb',
                  color: isLocked ? '#ffffff' : '#6b7280',
                }}
                data-testid="lock-compass-btn"
              >
                {isLocked ? <Lock size={16} /> : <Unlock size={16} />}
                {isLocked ? 'Unlock Compass' : 'Lock Compass'}
              </Button>
              <p style={{ fontSize: '12px', color: '#9ca3af', textAlign: 'center' }}>
                {isLocked ? 'Compass is locked in position' : 'Lock to prevent accidental moves'}
              </p>
            </div>
          )}

          {/* 2026 Flying Star Toggle */}
          {onToggleFlyingStar && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Star size={16} style={{ color: showFlyingStar ? '#9b59b6' : '#6b7280' }} />
                  <Label style={{ fontWeight: '500', color: '#1f2937' }}>
                    2026 Flying Star
                  </Label>
                  {!isPro && <span style={{ background: 'linear-gradient(135deg, #eab308, #f59e0b)', color: '#0f172a', padding: '1px 6px', borderRadius: '6px', fontSize: '9px', fontWeight: '700' }}>PRO</span>}
                </div>
                <Switch
                  checked={showFlyingStar}
                  onCheckedChange={onToggleFlyingStar}
                  data-testid="flying-star-toggle"
                />
              </div>
              <p style={{ fontSize: '12px', color: '#9ca3af' }}>
                Show annual Flying Star chart overlay
              </p>
              {showFlyingStar && onFlyingStarScaleChange && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Label style={{ fontSize: '13px', color: '#6b7280' }}>Chart Size</Label>
                    <span style={{ fontSize: '13px', color: '#1f2937', fontWeight: '500' }}>{Math.round(flyingStarScale * 100)}%</span>
                  </div>
                  <Slider
                    value={[flyingStarScale * 100]}
                    onValueChange={([v]) => onFlyingStarScaleChange(v / 100)}
                    min={50}
                    max={250}
                    step={5}
                    data-testid="flying-star-size-slider"
                  />
                </div>
              )}
            </div>
          )}

          {/* Rotation Control */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Label style={{ fontWeight: '500', color: '#1f2937' }}>
                Rotation
              </Label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Input
                  type="number"
                  value={rotation === 0 ? '0' : Math.round(rotation)}
                  onChange={handleRotationInput}
                  onBlur={(e) => {
                    let value = parseFloat(e.target.value) || 0;
                    value = Math.max(0, Math.min(360, value));
                    onRotationChange(value);
                  }}
                  style={{ 
                    width: '80px', 
                    height: '36px', 
                    textAlign: 'center', 
                    fontSize: '16px',
                    fontWeight: '600',
                    borderColor: '#2563eb',
                    borderWidth: '2px',
                  }}
                  min="0"
                  max="360"
                  data-testid="rotation-input"
                />
                <span style={{ fontSize: '16px', fontWeight: '600', color: '#2563eb' }}>°</span>
              </div>
            </div>
            
            {/* Quick degree presets */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(4, 1fr)', 
              gap: '6px' 
            }}>
              {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
                <button
                  key={deg}
                  onClick={() => onRotationChange(deg)}
                  style={{
                    padding: '6px 4px',
                    fontSize: '12px',
                    fontWeight: rotation === deg ? '600' : '400',
                    background: rotation === deg ? '#2563eb' : '#f3f4f6',
                    color: rotation === deg ? 'white' : '#6b7280',
                    border: '1px solid',
                    borderColor: rotation === deg ? '#2563eb' : '#e5e7eb',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {deg}°
                </button>
              ))}
            </div>
            
            <Slider
              value={[rotation]}
              onValueChange={([value]) => onRotationChange(value)}
              min={0}
              max={360}
              step={0.5}
              className="py-2"
              data-testid="rotation-slider"
            />
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              fontSize: '11px', 
              color: '#9ca3af' 
            }}>
              <span>0°</span>
              <span>90°</span>
              <span>180°</span>
              <span>270°</span>
              <span>360°</span>
            </div>
          </div>

          {/* Compass Toggle - Show/Hide entire compass */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Compass size={16} style={{ color: '#2563eb' }} />
                <Label style={{ fontWeight: '500', color: '#1f2937' }}>
                  Show Compass
                </Label>
              </div>
              <Switch
                checked={showCompass}
                onCheckedChange={onShowCompassChange}
                data-testid="show-compass-toggle"
              />
            </div>
            <p style={{ fontSize: '12px', color: '#9ca3af' }}>
              Turn off everything
            </p>
          </div>

          {/* Hide Compass (Show Torch Only) Toggle */}
          {onToggleCompassVisual && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Compass size={16} style={{ color: showCompassVisual ? '#2563eb' : '#6b7280' }} />
                  <Label style={{ fontWeight: '500', color: '#1f2937' }}>
                    Show Compass
                  </Label>
                </div>
                <Switch
                  checked={showCompassVisual}
                  onCheckedChange={onToggleCompassVisual}
                  data-testid="compass-visual-toggle"
                />
              </div>
              <p style={{ fontSize: '12px', color: '#9ca3af' }}>
                Turn off to show only torch light
              </p>
            </div>
          )}

          {/* Compass Size */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Maximize2 size={16} style={{ color: '#2563eb' }} />
                <Label style={{ fontWeight: '500', color: '#1f2937' }}>
                  Compass Size
                </Label>
              </div>
              <span style={{ fontSize: '13px', color: '#6b7280' }}>
                {compassSize}px
              </span>
            </div>
            <Slider
              value={[compassSize]}
              onValueChange={([value]) => onCompassSizeChange(value)}
              min={300}
              max={900}
              step={10}
              className="py-2"
              data-testid="compass-size-slider"
            />
          </div>

          {/* Opacity Control */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Eye size={16} style={{ color: '#2563eb' }} />
                <Label style={{ fontWeight: '500', color: '#1f2937' }}>
                  Opacity
                </Label>
              </div>
              <span style={{ fontSize: '13px', color: '#6b7280' }}>
                {Math.round((opacity || 1) * 100)}%
              </span>
            </div>
            <Slider
              value={[opacity * 100]}
              onValueChange={([value]) => onOpacityChange(value / 100)}
              min={0}
              max={100}
              step={5}
              className="py-2"
              data-testid="opacity-slider"
            />
          </div>

          {/* Floor Plan Rotation Control */}
          {onFloorPlanRotationChange && (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '12px',
              padding: '16px',
              background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
              borderRadius: '12px',
              border: '1px solid #f59e0b',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Label style={{ fontWeight: '600', color: '#92400e' }}>
                  Floor Plan Rotation
                </Label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <Input
                    type="number"
                    value={floorPlanRotation === 0 ? '0' : Math.round(floorPlanRotation)}
                    onChange={handleFloorPlanRotationInput}
                    style={{ 
                      width: '70px', 
                      height: '32px', 
                      textAlign: 'center', 
                      fontSize: '14px',
                      fontWeight: '600',
                      borderColor: '#f59e0b',
                      borderWidth: '2px',
                      background: 'white',
                    }}
                    min="-180"
                    max="180"
                    data-testid="floor-rotation-input"
                  />
                  <span style={{ fontSize: '14px', fontWeight: '600', color: '#92400e' }}>°</span>
                </div>
              </div>
              
              {/* Quick rotation presets */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(5, 1fr)', 
                gap: '4px' 
              }}>
                {[-90, -45, 0, 45, 90].map((deg) => (
                  <button
                    key={deg}
                    onClick={() => onFloorPlanRotationChange(deg)}
                    style={{
                      padding: '6px 2px',
                      fontSize: '11px',
                      fontWeight: floorPlanRotation === deg ? '600' : '400',
                      background: floorPlanRotation === deg ? '#f59e0b' : 'white',
                      color: floorPlanRotation === deg ? 'white' : '#92400e',
                      border: '1px solid',
                      borderColor: floorPlanRotation === deg ? '#f59e0b' : '#fcd34d',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                  >
                    {deg}°
                  </button>
                ))}
              </div>
              
              <Slider
                value={[floorPlanRotation]}
                onValueChange={([value]) => onFloorPlanRotationChange(value)}
                min={-180}
                max={180}
                step={1}
                className="py-2"
                data-testid="floor-rotation-slider"
              />
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                fontSize: '10px', 
                color: '#92400e' 
              }}>
                <span>-180°</span>
                <span>-90°</span>
                <span>0°</span>
                <span>90°</span>
                <span>180°</span>
              </div>
            </div>
          )}

          {/* Floor Plan Zoom Control */}
          {onFloorPlanZoomIn && onFloorPlanZoomOut && (
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '12px',
              padding: '16px',
              background: isFloorPlanLocked 
                ? 'linear-gradient(135deg, #fecaca, #fca5a5)' 
                : 'linear-gradient(135deg, #d1fae5, #a7f3d0)',
              borderRadius: '12px',
              border: `1px solid ${isFloorPlanLocked ? '#dc2626' : '#059669'}`,
              opacity: isFloorPlanLocked ? 0.7 : 1,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Image size={16} style={{ color: isFloorPlanLocked ? '#dc2626' : '#047857' }} />
                  <Label style={{ fontWeight: '600', color: isFloorPlanLocked ? '#dc2626' : '#047857' }}>
                    Floor Plan Zoom {isFloorPlanLocked && '(Locked)'}
                  </Label>
                </div>
                <div style={{ 
                  padding: '4px 12px',
                  backgroundColor: isFloorPlanLocked ? '#dc2626' : '#059669',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '700',
                  color: '#ffffff',
                }}>
                  {floorPlanZoom}%
                </div>
              </div>
              
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                gap: '12px' 
              }}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onFloorPlanZoomOut}
                  disabled={isFloorPlanLocked}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    padding: '10px',
                    backgroundColor: isFloorPlanLocked ? '#f5f5f5' : 'white',
                    borderColor: isFloorPlanLocked ? '#d1d5db' : '#059669',
                    color: isFloorPlanLocked ? '#9ca3af' : '#047857',
                    cursor: isFloorPlanLocked ? 'not-allowed' : 'pointer',
                  }}
                  data-testid="floor-zoom-out-btn"
                >
                  <ZoomOut size={18} />
                  <span>Zoom Out</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onFloorPlanZoomIn}
                  disabled={isFloorPlanLocked}
                  style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    padding: '10px',
                    backgroundColor: isFloorPlanLocked ? '#f5f5f5' : 'white',
                    borderColor: isFloorPlanLocked ? '#d1d5db' : '#059669',
                    color: isFloorPlanLocked ? '#9ca3af' : '#047857',
                    cursor: isFloorPlanLocked ? 'not-allowed' : 'pointer',
                  }}
                  data-testid="floor-zoom-in-btn"
                >
                  <ZoomIn size={18} />
                  <span>Zoom In</span>
                </Button>
              </div>
              
              <p style={{ fontSize: '11px', color: isFloorPlanLocked ? '#9ca3af' : '#059669', textAlign: 'center' }}>
                {isFloorPlanLocked ? 'Unlock floor plan to zoom' : 'Use buttons or mouse wheel to zoom'}
              </p>
            </div>
          )}

          {/* Center Lines Toggle */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Grid3X3 size={16} style={{ color: '#2563eb' }} />
                <Label style={{ fontWeight: '500', color: '#1f2937' }}>
                  Center Lines
                </Label>
              </div>
              <Switch
                checked={showCenterLines}
                onCheckedChange={onShowCenterLinesChange}
                data-testid="show-center-lines-toggle"
              />
            </div>
            <p style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>
              {confirmedBox 
                ? '✓ Custom center point set' 
                : centerBox 
                  ? 'Box drawn - click Confirm to set center' 
                  : 'Draw a box to define custom center point'}
            </p>
            
            {/* Draw/Confirm/Clear Center Box Buttons */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {/* Draw Box Button - show when no box is drawn */}
              {!centerBox && !confirmedBox && (
                <button
                  onClick={onStartDrawCenterBox}
                  disabled={isDrawingCenterBox}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    fontSize: '12px',
                    fontWeight: '500',
                    backgroundColor: isDrawingCenterBox ? '#dbeafe' : '#eff6ff',
                    color: '#2563eb',
                    border: '1px solid #bfdbfe',
                    borderRadius: '6px',
                    cursor: isDrawingCenterBox ? 'default' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                  }}
                >
                  <Crosshair size={14} />
                  {isDrawingCenterBox ? 'Drawing...' : 'Draw Box'}
                </button>
              )}
              
              {/* When box is drawn but not confirmed - show Confirm and Cancel */}
              {centerBox && (
                <>
                  <button
                    onClick={onConfirmCenterBox}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      fontSize: '12px',
                      fontWeight: '600',
                      backgroundColor: '#059669',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                    }}
                  >
                    ✓ Confirm
                  </button>
                  <button
                    onClick={onClearCenterBox}
                    style={{
                      padding: '8px 12px',
                      fontSize: '12px',
                      fontWeight: '500',
                      backgroundColor: '#fef2f2',
                      color: '#dc2626',
                      border: '1px solid #fecaca',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                    }}
                  >
                    <X size={14} />
                    Cancel
                  </button>
                </>
              )}
              
              {/* When center is confirmed - show Redraw option */}
              {confirmedBox && !centerBox && (
                <>
                  <button
                    onClick={onStartDrawCenterBox}
                    disabled={isDrawingCenterBox}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      fontSize: '12px',
                      fontWeight: '500',
                      backgroundColor: isDrawingCenterBox ? '#dbeafe' : '#eff6ff',
                      color: '#2563eb',
                      border: '1px solid #bfdbfe',
                      borderRadius: '6px',
                      cursor: isDrawingCenterBox ? 'default' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                    }}
                  >
                    <Crosshair size={14} />
                    {isDrawingCenterBox ? 'Drawing...' : 'Redraw'}
                  </button>
                  <button
                    onClick={onClearConfirmedBox}
                    style={{
                      padding: '8px 12px',
                      fontSize: '12px',
                      fontWeight: '500',
                      backgroundColor: '#fef2f2',
                      color: '#dc2626',
                      border: '1px solid #fecaca',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px',
                    }}
                  >
                    <X size={14} />
                    Reset
                  </button>
                </>
              )}
            </div>
          </div>

          {/* 24 Mountains Highlight Toggle */}
          {onToggleHighlight && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Flashlight size={16} style={{ color: '#f59e0b' }} />
                <Label style={{ fontWeight: '500', color: '#1f2937' }}>
                  Highlight Directions
                </Label>
                {highlightedDirections.length > 0 && (
                  <span style={{ 
                    fontSize: '11px', 
                    padding: '2px 8px', 
                    background: '#fef3c7', 
                    color: '#d97706',
                    borderRadius: '12px',
                    fontWeight: '500'
                  }}>
                    {highlightedDirections.length} selected
                  </span>
                )}
              </div>
              <p style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '8px' }}>
                Click to highlight directions (torch light effect)
              </p>
              
              {/* 24 Mountains Grid */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(6, 1fr)', 
                gap: '4px',
                maxHeight: '200px',
                overflowY: 'auto',
                padding: '4px',
                background: '#f9fafb',
                borderRadius: '8px',
                border: '1px solid #e5e7eb'
              }}>
                {MOUNTAINS_24.map((mountain) => {
                  const isHighlighted = highlightedDirections.includes(mountain.index);
                  const isLocked = !isPro && mountain.index >= 12;
                  return (
                    <button
                      key={mountain.index}
                      onClick={() => onToggleHighlight(mountain.index)}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '6px 2px',
                        fontSize: '10px',
                        border: isHighlighted ? '2px solid #f59e0b' : '1px solid #e5e7eb',
                        borderRadius: '6px',
                        background: isLocked ? '#f1f5f9' : (isHighlighted ? '#fef3c7' : '#ffffff'),
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        minHeight: '44px',
                        opacity: isLocked ? 0.5 : 1,
                        position: 'relative',
                      }}
                      title={isLocked ? `PRO: ${mountain.pinyin} (${mountain.direction})` : `${mountain.pinyin} (${mountain.direction})`}
                    >
                      <span style={{ 
                        fontSize: '14px', 
                        fontWeight: '600',
                        color: isHighlighted ? '#d97706' : '#374151',
                        fontFamily: "'Noto Sans SC', sans-serif"
                      }}>
                        {mountain.name}
                      </span>
                      <span style={{ 
                        fontSize: '8px', 
                        color: isHighlighted ? '#b45309' : '#9ca3af',
                        marginTop: '2px'
                      }}>
                        {mountain.direction}
                      </span>
                      {isLocked && (
                        <span style={{ position: 'absolute', top: '2px', right: '2px', fontSize: '7px', background: '#eab308', color: '#0f172a', padding: '0 3px', borderRadius: '3px', fontWeight: '700', lineHeight: '12px' }}>PRO</span>
                      )}
                    </button>
                  );
                })}
              </div>
              
              {/* Clear All Button */}
              {highlightedDirections.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    highlightedDirections.forEach(idx => onToggleHighlight(idx));
                  }}
                  style={{ 
                    width: '100%',
                    fontSize: '12px',
                    marginTop: '8px',
                    color: '#6b7280'
                  }}
                >
                  Clear All Highlights
                </Button>
              )}
            </div>
          )}

          {/* Personal Kua Analysis Section */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Sparkles size={16} style={{ color: '#8b5cf6' }} />
                <Label style={{ fontWeight: '500', color: '#1f2937' }}>
                  Analyze (八宅)
                </Label>
                {!isPro && <span style={{ background: 'linear-gradient(135deg, #eab308, #f59e0b)', color: '#0f172a', padding: '1px 6px', borderRadius: '6px', fontSize: '9px', fontWeight: '700' }}>PRO</span>}
              </div>
              <Switch
                checked={kuaAnalysisEnabled}
                onCheckedChange={onKuaAnalysisToggle}
                data-testid="kua-analysis-toggle"
              />
            </div>
            <p style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '4px' }}>
              Eight Mansions personal direction analysis
            </p>
            
            {kuaAnalysisEnabled && (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '12px',
                padding: '12px',
                background: '#f5f3ff',
                borderRadius: '8px',
                border: '1px solid #c4b5fd'
              }}>
                {/* Birth Date Input */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Label style={{ fontSize: '12px', color: '#6b7280' }}>Date of Birth 出生日期</Label>
                  <Input
                    type="date"
                    value={birthDate}
                    onChange={(e) => onBirthDateChange(e.target.value)}
                    style={{ 
                      height: '36px',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                    data-testid="birth-date-input"
                  />
                  <p style={{ fontSize: '10px', color: '#9ca3af', marginTop: '2px' }}>
                    Chinese New Year is considered for accurate calculation
                  </p>
                </div>
                
                {/* Gender Selection */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Label style={{ fontSize: '12px', color: '#6b7280' }}>Gender 性別</Label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => onGenderChange('male')}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        fontSize: '13px',
                        fontWeight: '500',
                        backgroundColor: gender === 'male' ? '#3b82f6' : '#ffffff',
                        color: gender === 'male' ? '#ffffff' : '#6b7280',
                        border: `1px solid ${gender === 'male' ? '#3b82f6' : '#d1d5db'}`,
                        borderRadius: '6px',
                        cursor: 'pointer',
                      }}
                      data-testid="gender-male-btn"
                    >
                      Male 男
                    </button>
                    <button
                      onClick={() => onGenderChange('female')}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        fontSize: '13px',
                        fontWeight: '500',
                        backgroundColor: gender === 'female' ? '#ec4899' : '#ffffff',
                        color: gender === 'female' ? '#ffffff' : '#6b7280',
                        border: `1px solid ${gender === 'female' ? '#ec4899' : '#d1d5db'}`,
                        borderRadius: '6px',
                        cursor: 'pointer',
                      }}
                      data-testid="gender-female-btn"
                    >
                      Female 女
                    </button>
                  </div>
                </div>
                
                {/* Kua Number Display */}
                {kuaNumber && (
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: '8px',
                    padding: '12px',
                    background: '#ffffff',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '13px', color: '#6b7280' }}>Kua Number 命卦</span>
                      <span style={{ 
                        fontSize: '20px', 
                        fontWeight: '700',
                        color: getKuaGroup(kuaNumber) === 'east' ? '#059669' : '#dc2626'
                      }}>
                        {kuaNumber}
                      </span>
                    </div>
                    <div style={{ fontSize: '11px', color: '#9ca3af', textAlign: 'center' }}>
                      Chinese Year: {getChineseYear(birthDate)}
                    </div>
                    <div style={{ 
                      fontSize: '12px', 
                      color: getKuaGroup(kuaNumber) === 'east' ? '#059669' : '#dc2626',
                      fontWeight: '500',
                      textAlign: 'center',
                      padding: '4px 8px',
                      background: getKuaGroup(kuaNumber) === 'east' ? '#ecfdf5' : '#fef2f2',
                      borderRadius: '4px'
                    }}>
                      {getKuaGroup(kuaNumber) === 'east' ? '東四命 East Group' : '西四命 West Group'}
                    </div>
                  </div>
                )}
                
                {/* Direction Legend */}
                {kuaDirections && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <Label style={{ fontSize: '12px', color: '#6b7280' }}>Lucky Directions 吉方位 <span style={{ fontSize: '10px', color: '#9ca3af' }}>(click for details)</span></Label>
                    
                    {/* Auspicious Directions - sorted by rank */}
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      gap: '6px' 
                    }}>
                      {Object.entries(kuaDirections)
                        .filter(([_, info]) => info.isAuspicious)
                        .sort((a, b) => a[1].type.rank - b[1].type.rank)
                        .map(([dir, info], index) => {
                          const explanation = LUCKY_EXPLANATIONS[info.type.rank];
                          return (
                          <div 
                            key={dir}
                            onClick={() => {
                              alert(`${explanation.title}\n\n${explanation.text}`);
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              padding: '10px 12px',
                              background: '#ecfdf5',
                              borderRadius: '8px',
                              borderLeft: `4px solid ${info.type.color}`,
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              position: 'relative'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#d1fae5';
                              e.currentTarget.style.transform = 'scale(1.01)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#ecfdf5';
                              e.currentTarget.style.transform = 'scale(1)';
                            }}
                          >
                            {/* Rank number */}
                            <div style={{
                              width: '24px',
                              height: '24px',
                              borderRadius: '50%',
                              background: info.type.color,
                              color: '#ffffff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '12px',
                              fontWeight: '700',
                              flexShrink: 0
                            }}>
                              {info.type.rank}
                            </div>
                            {/* Direction info */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '13px', fontWeight: '700', color: '#059669' }}>
                                  {info.name}
                                </span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>
                                    {dir}
                                  </span>
                                  <Info size={12} style={{ color: '#9ca3af' }} />
                                </div>
                              </div>
                              <div style={{ fontSize: '11px', color: '#374151', marginTop: '2px' }}>
                                {info.element} • {info.lifeAspect}
                              </div>
                              <div style={{ fontSize: '11px', color: info.type.color, fontWeight: '600', marginTop: '2px' }}>
                                {info.type.name} {info.type.chinese} - {explanation.title}
                              </div>
                            </div>
                          </div>
                        )})
                      }
                    </div>
                    
                    <Label style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>Unlucky Directions 凶方位 <span style={{ fontSize: '10px', color: '#9ca3af' }}>(click for details)</span></Label>
                    
                    {/* Inauspicious Directions - sorted by rank (worst first) */}
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      gap: '6px' 
                    }}>
                      {Object.entries(kuaDirections)
                        .filter(([_, info]) => !info.isAuspicious)
                        .sort((a, b) => a[1].type.rank - b[1].type.rank)
                        .map(([dir, info], index) => {
                          const explanation = UNLUCKY_EXPLANATIONS[info.type.rank];
                          return (
                          <div 
                            key={dir}
                            onClick={() => {
                              alert(`${explanation.title}\n\n${explanation.text}`);
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              padding: '10px 12px',
                              background: '#fef2f2',
                              borderRadius: '8px',
                              borderLeft: `4px solid ${info.type.color}`,
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#fee2e2';
                              e.currentTarget.style.transform = 'scale(1.01)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = '#fef2f2';
                              e.currentTarget.style.transform = 'scale(1)';
                            }}
                          >
                            {/* Rank number */}
                            <div style={{
                              width: '24px',
                              height: '24px',
                              borderRadius: '50%',
                              background: info.type.color,
                              color: '#ffffff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '12px',
                              fontWeight: '700',
                              flexShrink: 0
                            }}>
                              {info.type.rank}
                            </div>
                            {/* Direction info */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '13px', fontWeight: '700', color: '#dc2626' }}>
                                  {info.name}
                                </span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                  <span style={{ fontSize: '11px', color: '#6b7280', fontWeight: '500' }}>
                                    {dir}
                                  </span>
                                  <Info size={12} style={{ color: '#9ca3af' }} />
                                </div>
                              </div>
                              <div style={{ fontSize: '11px', color: '#374151', marginTop: '2px' }}>
                                {info.element} • {info.lifeAspect}
                              </div>
                              <div style={{ fontSize: '11px', color: info.type.color, fontWeight: '600', marginTop: '2px' }}>
                                {info.type.name} {info.type.chinese} - {explanation.title}
                              </div>
                            </div>
                          </div>
                        )})
                      }
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '12px', 
            paddingTop: '16px', 
            borderTop: '1px solid #e5e7eb' 
          }}>
            <Button
              onClick={onExport}
              style={{ 
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                backgroundColor: '#2563eb',
                color: '#ffffff',
              }}
              data-testid="export-btn"
            >
              <Download size={16} />
              Export Image
            </Button>
            
            <Button
              onClick={onSave}
              variant="outline"
              disabled={!isPro}
              style={{ 
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                opacity: isPro ? 1 : 0.5,
              }}
              data-testid="save-project-panel-btn"
            >
              <Save size={16} style={{ color: '#2563eb' }} />
              Save Project
              {!isPro && <span style={{ background: 'linear-gradient(135deg, #eab308, #f59e0b)', color: '#0f172a', padding: '0 5px', borderRadius: '4px', fontSize: '9px', fontWeight: '700' }}>PRO</span>}
            </Button>
          </div>

          {/* Footer */}
          <div style={{ 
            textAlign: 'center', 
            paddingTop: '16px', 
            borderTop: '1px solid #e5e7eb' 
          }}>
            <p style={{ fontSize: '12px', color: '#9ca3af' }}>
              24 Mountains Feng Shui Compass
            </p>
            <p style={{ fontSize: '11px', color: '#d1d5db', marginTop: '4px' }}>
              二十四山羅盤
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ControlPanel;
